/**
 * «Написать пост» — вход в авторские посты (§3.2 спецификации).
 * Вверху выбор проекта: «Новый пост» создаёт черновик и открывает редактор.
 * Ниже свои посты: черновики, запланированные (по времени) и коротко — недавно опубликованные.
 */
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router";
import { CalendarClock, ExternalLink, PenLine, Plus, Trash2 } from "lucide-react";
import type { JobSummary, Post, PostFull, Project } from "@impuls/shared";
import { api, ApiError, errText } from "../lib/api";
import { ago, countText, dateTime, dayMonth, humanDateTime, sameDay, time } from "../lib/format";
import { useProjects, useRefreshPosts } from "../lib/queries";
import { EcgLoader } from "../components/Ecg";
import { StatusChip } from "../components/PostBits";
import { EmptyState, LoadError, PageHeader, useConfirm, useToast } from "../components/ui";

const RECENT_LIMIT = 5;
/** Сколько строк показывать сразу — остальное по кнопке (черновики) или в календаре (расписание). */
const DRAFTS_VISIBLE = 8;
const SCHEDULED_VISIBLE = 6;

/** Свои (авторские) посты с нужным статусом. Ключ начинается с "posts" — его обновляет общий поток. */
const useAuthorPosts = (status: string, limit?: number) =>
  useQuery({
    queryKey: ["posts", "author", status, limit ?? null],
    queryFn: () => api.get<Post[]>(`/api/posts?group=author&status=${status}${limit ? `&limit=${limit}` : ""}`),
  });

/** Идущие задачи — чтобы показать «Claude пишет пост…» прямо в строке черновика. Ключ ["jobs"] обновляет общий поток. */
const useRunningJobs = () =>
  useQuery({ queryKey: ["jobs", "running"], queryFn: () => api.get<JobSummary[]>("/api/jobs/running"), retry: false });

export function WritePage() {
  const { data: projects, error: projectsError, isLoading: projectsLoading, refetch: refetchProjects } = useProjects("0");
  const drafts = useAuthorPosts("draft,failed");
  const scheduled = useAuthorPosts("scheduled");
  const recent = useAuthorPosts("published", RECENT_LIMIT);
  const { data: running } = useRunningJobs();
  const { create, pending } = useCreatePost();
  const [allDrafts, setAllDrafts] = useState(false);

  // рубрика проекта — первая строка поста; в «начале текста» её не повторяем
  const rubrics = useMemo(() => new Map((projects ?? []).map((p) => [p.id, p.rubric])), [projects]);
  const jobsByPost = useMemo(() => {
    const m = new Map<number, JobSummary>();
    for (const j of running ?? []) if (j.post_id && (j.type === "author_turn" || !m.has(j.post_id))) m.set(j.post_id, j);
    return m;
  }, [running]);
  const general = projects?.find((p) => p.is_general);

  return (
    <div className="page write">
      <PageHeader title="Написать пост" />

      <section className="write-sec" aria-labelledby="write-projects-title">
        <div className="write-sec__head">
          <h2 id="write-projects-title" className="write-sec__title">
            Выберите проект
          </h2>
        </div>
        {projectsLoading ? (
          <div className="write-loading sheet">
            <EcgLoader label="Загружаю проекты" />
          </div>
        ) : projectsError ? (
          <LoadError error={projectsError} retry={() => void refetchProjects()} />
        ) : (
          <ProjectPicker projects={projects ?? []} pending={pending} onCreate={create} />
        )}
      </section>

      <div className="write-lists">
        <section className="write-sec write-sec--drafts" aria-labelledby="write-drafts-title">
          <div className="write-sec__head">
            <h2 id="write-drafts-title" className="write-sec__title">
              Черновики
              {!!drafts.data?.length && <span className="write-sec__count">{drafts.data.length}</span>}
            </h2>
          </div>
          {drafts.isLoading ? (
            <div className="write-loading panel">
              <EcgLoader label="Загружаю черновики" />
            </div>
          ) : drafts.error ? (
            <LoadError error={drafts.error} retry={() => void drafts.refetch()} />
          ) : !drafts.data?.length ? (
            <EmptyState
              mascot="hug"
              title="Черновиков нет"
              action={
                <button className="btn btn--primary" onClick={() => create(general?.id ?? null)} disabled={pending !== null}>
                  <Plus size={18} />
                  {pending !== null ? "Создаю…" : "Новый пост"}
                </button>
              }
            />
          ) : (
            <div className="write-list panel">
              <ul className="write-list__rows">
                {(allDrafts ? drafts.data : drafts.data.slice(0, DRAFTS_VISIBLE)).map((p) => (
                  <DraftRow key={p.id} post={p} rubric={p.project_id ? rubrics.get(p.project_id) : undefined} job={jobsByPost.get(p.id)} />
                ))}
              </ul>
              {!allDrafts && drafts.data.length > DRAFTS_VISIBLE && (
                <button className="write-list__more" onClick={() => setAllDrafts(true)}>
                  Показать ещё {drafts.data.length - DRAFTS_VISIBLE}
                </button>
              )}
            </div>
          )}
        </section>

        <div className="write-side">
          <section className="write-sec" aria-labelledby="write-scheduled-title">
            <div className="write-sec__head">
              <h2 id="write-scheduled-title" className="write-sec__title">
                Запланированы
                {!!scheduled.data?.length && <span className="write-sec__count write-sec__count--wine">{scheduled.data.length}</span>}
              </h2>
              {!!scheduled.data?.length && (
                <Link className="write-sec__link" to="/calendar">
                  Календарь
                </Link>
              )}
            </div>
            {scheduled.isLoading ? (
              <div className="write-loading panel">
                <EcgLoader label="Загружаю расписание" />
              </div>
            ) : scheduled.error ? (
              <InlineError error={scheduled.error} retry={() => void scheduled.refetch()} />
            ) : !scheduled.data?.length ? (
              <p className="write-quiet panel">
                <CalendarClock size={18} aria-hidden="true" />
                <span>Ничего не запланировано</span>
              </p>
            ) : (
              <div className="write-list panel">
                <ul className="write-list__rows">
                  {scheduled.data.slice(0, SCHEDULED_VISIBLE).map((p) => (
                    <ScheduledRow
                      key={p.id}
                      post={p}
                      rubric={p.project_id ? rubrics.get(p.project_id) : undefined}
                      job={jobsByPost.get(p.id)}
                    />
                  ))}
                </ul>
                {scheduled.data.length > SCHEDULED_VISIBLE && (
                  <Link className="write-list__more" to="/calendar">
                    Ещё {countText(scheduled.data.length - SCHEDULED_VISIBLE, "пост", "поста", "постов")} в календаре
                  </Link>
                )}
              </div>
            )}
          </section>

          {(recent.error || !!recent.data?.length) && (
            <section className="write-sec" aria-labelledby="write-recent-title">
              <div className="write-sec__head">
                <h2 id="write-recent-title" className="write-sec__title">
                  Недавно опубликованы
                </h2>
              </div>
              {recent.error ? (
                <InlineError error={recent.error} retry={() => void recent.refetch()} />
              ) : (
                <ul className="write-recent">
                  {recent.data!.map((p) => (
                    <RecentRow key={p.id} post={p} />
                  ))}
                </ul>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

/* ================= Новый пост ================= */
type Pending = number | "none" | null;

function useCreatePost() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const refresh = useRefreshPosts();
  const [pending, setPending] = useState<Pending>(null);
  const create = async (projectId: number | null) => {
    if (pending !== null) return;
    setPending(projectId ?? "none");
    try {
      const post = await api.post<PostFull>("/api/posts", { project_id: projectId });
      qc.setQueryData(["post", post.id], post);
      refresh();
      nav(`/posts/${post.id}`);
    } catch (e) {
      toast(errText(e), "error");
      setPending(null);
    }
  };
  return { create, pending };
}

function ProjectPicker({ projects, pending, onCreate }: { projects: Project[]; pending: Pending; onCreate: (id: number | null) => void }) {
  if (!projects.length) {
    return (
      <EmptyState
        mascot="wave"
        title="Активных проектов нет"
        action={
          <div className="row">
            <Link className="btn btn--primary" to="/projects">
              Открыть проекты
            </Link>
            <button className="btn" onClick={() => onCreate(null)} disabled={pending !== null}>
              {pending === "none" ? "Создаю…" : "Пост без проекта"}
            </button>
          </div>
        }
      />
    );
  }
  return (
    <div className="write-projects sheet">
      <ul className="write-projects__list">
        {projects.map((p) => (
          <li key={p.id}>
            <button className="write-proj" onClick={() => onCreate(p.id)} disabled={pending !== null} aria-busy={pending === p.id}>
              <span className="write-proj__body">
                <span className="write-proj__name">
                  {p.name}
                  {p.is_general && <span className="chip chip--outline write-proj__mark">Общий</span>}
                </span>
                {p.rubric ? (
                  <span className="write-proj__rubric">{p.rubric}</span>
                ) : (
                  <span className="write-proj__rubric write-proj__rubric--none">{p.footer ? "Без рубрики" : "Без рубрики и плашки"}</span>
                )}
                {p.hashtags && <span className="write-proj__tags">{p.hashtags}</span>}
              </span>
              <span className={`write-proj__go ${pending === p.id ? "is-busy" : ""}`}>
                {pending === p.id ? (
                  <EcgLoader size={40} tone="white" label="Создаю" />
                ) : (
                  <>
                    <Plus size={18} aria-hidden="true" />
                    Новый пост
                  </>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <div className="write-projects__foot">
        <button className="btn btn--ghost write-projects__none" onClick={() => onCreate(null)} disabled={pending !== null}>
          <PenLine size={17} aria-hidden="true" />
          {pending === "none" ? "Создаю…" : "Пост без проекта"}
        </button>
        <Link className="write-projects__hint" to="/projects?new=1">
          Создать проект
        </Link>
      </div>
    </div>
  );
}

/* ================= Строки постов ================= */

/** Начало текста без рубрики, разметки ВК и лишних переносов. */
function textStart(text: string, rubric?: string): string {
  const lines = text
    .replace(/\[(?:club|public|id)\d+\|([^\]]+)\]/g, "$1")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
  if (rubric && lines[0] === rubric.trim()) lines.shift();
  return lines.join(" ").slice(0, 280);
}

function LiveJob({ job }: { job: JobSummary }) {
  return <EcgLoader size={40} className="write-live" label={job.type === "image" ? "Рисую иллюстрацию…" : "Claude пишет пост…"} />;
}

function DraftRow({ post, rubric, job }: { post: Post; rubric?: string; job?: JobSummary }) {
  const toast = useToast();
  const confirm = useConfirm();
  const refresh = useRefreshPosts();
  const [deleting, setDeleting] = useState(false);
  const start = textStart(post.text, rubric);
  const thumb = post.images[0]?.url;

  const remove = async () => {
    const ok = await confirm({
      title: "Удалить черновик?",
      text: `«${post.title}» исчезнет вместе с чатом и фото.`,
      confirm: "Удалить",
      danger: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await api.del(`/api/posts/${post.id}`);
      toast("Черновик удалён");
      refresh();
    } catch (e) {
      if (e instanceof ApiError && e.code === "vk_manual") {
        const force = await confirm({
          title: "Удалите запись во ВКонтакте вручную",
          text: e.message,
          confirm: "Я удалил во ВК — удалить пост",
          danger: true,
        });
        if (force) {
          try {
            await api.del(`/api/posts/${post.id}?force=1`);
            toast("Черновик удалён");
            refresh();
          } catch (err) {
            toast(errText(err), "error");
          }
        }
      } else toast(errText(e), "error");
      setDeleting(false);
    }
  };

  return (
    <li className={`write-post ${deleting ? "is-leaving" : ""}`}>
      <div className="write-post__main">
        <h3 className="write-post__title">
          <Link className="write-post__link" to={`/posts/${post.id}`}>
            {post.title}
          </Link>
        </h3>
        <p className={`write-post__text ${start ? "" : "is-empty"}`}>{start || "Пустой черновик"}</p>
        <div className="write-post__meta">
          <span className="write-post__project">{post.project_name ?? "Без проекта"}</span>
          <time className="write-post__time" dateTime={new Date(post.updated_at).toISOString()} title={dateTime(post.updated_at)}>
            изменён {ago(post.updated_at)}
          </time>
          {post.images.length > 1 && <span className="write-post__count">{post.images.length} фото</span>}
          {job ? <LiveJob job={job} /> : <StatusChip status={post.status} />}
        </div>
        {post.status === "failed" && post.error && <p className="write-post__error">{post.error}</p>}
      </div>
      {thumb && <img className="write-post__thumb" src={thumb} alt="" loading="lazy" />}
      <button
        className="icon-btn icon-btn--danger write-post__del"
        onClick={remove}
        disabled={deleting}
        aria-label={`Удалить черновик «${post.title}»`}
        title="Удалить черновик"
      >
        <Trash2 size={18} />
      </button>
    </li>
  );
}

/** «сегодня» / «завтра» / «22 октября» — день отдельно от времени. */
function dayLabel(ms: number): string {
  const d = new Date(ms);
  const today = new Date();
  if (sameDay(d, today)) return "сегодня";
  if (sameDay(d, new Date(today.getTime() + 864e5))) return "завтра";
  return dayMonth(ms);
}

function ScheduledRow({ post, rubric, job }: { post: Post; rubric?: string; job?: JobSummary }) {
  const at = (post.scheduled_at ?? 0) * 1000;
  const start = textStart(post.text, rubric);
  return (
    <li className="write-post write-post--scheduled">
      <time className="write-when" dateTime={new Date(at).toISOString()} title={dateTime(at)}>
        <span className="write-when__day">{dayLabel(at)}</span>
        <span className="write-when__time">{time(at)}</span>
      </time>
      <div className="write-post__main">
        <h3 className="write-post__title">
          <Link className="write-post__link" to={`/posts/${post.id}`}>
            {post.title}
          </Link>
        </h3>
        {start && <p className="write-post__text write-post__text--short">{start}</p>}
        <div className="write-post__meta">
          <span className="write-post__project">{post.project_name ?? "Без проекта"}</span>
          {post.images.length > 0 && <span className="write-post__count">{post.images.length} фото</span>}
          {job && <LiveJob job={job} />}
          {post.vk_dirty && <span className="chip chip--warn">Правки не отправлены во ВК</span>}
        </div>
      </div>
    </li>
  );
}

function RecentRow({ post }: { post: Post }) {
  const at = post.published_at ? post.published_at * 1000 : post.updated_at;
  return (
    <li className="write-recent__row">
      <div className="write-recent__main">
        <Link className="write-recent__title" to={`/posts/${post.id}`}>
          {post.title}
        </Link>
        <span className="write-recent__sub">
          <span className="write-post__project">{post.project_name ?? "Без проекта"}</span>
          <time dateTime={new Date(at).toISOString()} title={dateTime(at)}>
            {humanDateTime(at)}
          </time>
          {post.vk_text_only && <span className="chip chip--warn">Без фото</span>}
        </span>
      </div>
      {post.vk_url && (
        <a
          className="write-recent__vk"
          href={post.vk_url}
          target="_blank"
          rel="noreferrer"
          aria-label={`«${post.title}» во ВКонтакте, откроется в новой вкладке`}
        >
          ВКонтакте
          <ExternalLink size={15} aria-hidden="true" />
        </a>
      )}
    </li>
  );
}

function InlineError({ error, retry }: { error: unknown; retry: () => void }) {
  return (
    <div className="error-line write-error">
      <span className="grow">{errText(error)}</span>
      <button className="btn btn--small" onClick={retry}>
        Повторить
      </button>
    </div>
  );
}
